import type { DraggableAttributes } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { cn } from '../../lib/utils'

type DragHandleProps = {
	attributes?: DraggableAttributes
	listeners?: SyntheticListenerMap | undefined
}

export type SortableListRenderItemParams = {
	dragHandleProps: DragHandleProps
	isDragging: boolean
}

const SortableListRow = <T,>({
	item,
	getId,
	isItemSortable,
	renderItem,
}: {
	item: T
	getId: (item: T) => string
	isItemSortable?: (item: T) => boolean
	renderItem: (item: T, params: SortableListRenderItemParams) => React.ReactNode
}) => {
	const sortable = isItemSortable?.(item) ?? true

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: getId(item),
		disabled: !sortable,
	})

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: isDragging ? undefined : CSS.Transform.toString(transform),
				opacity: isDragging ? 0 : 1,
				visibility: isDragging ? 'hidden' : 'visible',
				transition,
			}}
		>
			{renderItem(item, {
				dragHandleProps: sortable ? { attributes, listeners } : {},
				isDragging,
			})}
		</div>
	)
}

export const SortableList = <T,>({
	value,
	onChange,
	getId,
	isItemSortable,
	renderItem,
	renderOverlay,
	className,
}: {
	value: T[]
	onChange: (next: T[]) => void
	getId: (item: T) => string
	isItemSortable?: (item: T) => boolean
	renderItem: (item: T, params: SortableListRenderItemParams) => React.ReactNode
	renderOverlay?: (item: T) => React.ReactNode
	className?: string
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	const [activeId, setActiveId] = useState<string | null>(null)
	const ids = useMemo(() => value.map((item) => getId(item)), [value, getId])

	const byId = useMemo(
		() => new Map(value.map((item) => [getId(item), item] as const)),
		[value, getId],
	)

	const activeItem = useMemo(() => {
		if (!activeId) return undefined
		return byId.get(activeId)
	}, [activeId, byId])

	const onDragStart = useCallback((event: DragStartEvent) => {
		setActiveId(event.active.id as string)
	}, [])

	const onDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			setActiveId(null)
			if (!over || active.id === over.id) return

			const oldIndex = ids.indexOf(active.id as string)
			const newIndex = ids.indexOf(over.id as string)

			if (oldIndex === -1 || newIndex === -1) return
			onChange(arrayMove(value, oldIndex, newIndex))
		},
		[ids, onChange, value],
	)

	return (
		<div className={cn('relative flex flex-col gap-2', className)}>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragStart={onDragStart}
				onDragEnd={onDragEnd}
			>
				<SortableContext
					items={ids}
					strategy={verticalListSortingStrategy}
				>
					{value.map((item) => (
						<SortableListRow
							getId={getId}
							isItemSortable={isItemSortable}
							item={item}
							key={getId(item)}
							renderItem={renderItem}
						/>
					))}
				</SortableContext>

				{typeof document === 'undefined'
					? null
					: createPortal(
							<DragOverlay adjustScale={false}>
								{!!activeItem &&
									(renderOverlay
										? renderOverlay(activeItem)
										: renderItem(activeItem, {
												dragHandleProps: {},
												isDragging: true,
											}))}
							</DragOverlay>,
							document.body,
						)}
			</DndContext>
		</div>
	)
}
